<script setup lang="ts">
import { useWindowSize } from "@vueuse/core"

import grip from "~/assets/icons/grip.svg"
import { propertyIcon, PropertyId } from "~/classes/entity"
import { PropertyFilterType, PropertySize, ViewModeType } from "~/classes/generated/interface"

const {
  state: viewModeState,
  viewModes,
  showableProperties,
  viewModePropertySizes,
  isShowingProperty,
  isSortingProperty,
  isFilteringProperty,
  showViewModeProperty,
  hideViewModeProperty,
  addViewModeSort,
  removeViewModeSort,
  rearrangeViewModeProperty,
  addViewModeFilter,
  removeViewModeFilter,
  updateGalleryViewSettings,
  updatePreviewScale,
  updatePropertyColor,
  updatePropertySize,
} = useViewMode()
const { switchCollectionViewMode } = useCollectionView()
const showablePropertyOptions = Object.values(showableProperties)
const sortablePropertyOptions = Object
  .values(showableProperties)
  .filter(p => "sortable" in p && p.sortable)
const filterablePropertyOptions = Object
  .values(showableProperties)
  .filter(p => "filterable" in p && p.filterable)

function onToggleShowProperty(propertyId: PropertyId) {
  isShowingProperty(propertyId)
    ? hideViewModeProperty(propertyId)
    : showViewModeProperty(propertyId)
}
function onToggleSortProperty(propertyId: PropertyId) {
  isSortingProperty(propertyId)
    ? removeViewModeSort(propertyId)
    : addViewModeSort({ propertyId, descending: false })
}
function onToggleFilterProperty(propertyId: PropertyId) {
  isSortingProperty(propertyId)
    ? removeViewModeFilter(propertyId)
    : addViewModeFilter({ propertyId, type: PropertyFilterType.Exists })
}
const { width } = useWindowSize()

const dragState = ref({
  dragShowIndex: undefined as number | undefined,
  dragShowNewIndex: undefined as number | undefined,
})

function startDragShow(event: DragEvent, index: number) {
  dragState.value.dragShowIndex = index
  event.dataTransfer!.effectAllowed = "move"
  event.dataTransfer!.setDragImage(event.target as Element, 0, 0)
}

function enterDragShow(event: DragEvent, index: number) {
  event.preventDefault()
  dragState.value.dragShowNewIndex = index
}

function endDragShow(_event: DragEvent) {
  if (dragState.value.dragShowIndex === undefined || dragState.value.dragShowNewIndex === undefined)
    return console.warn("Nowhere to drag")
  rearrangeViewModeProperty(dragState.value.dragShowIndex, dragState.value.dragShowNewIndex)
  dragState.value.dragShowIndex = undefined
  dragState.value.dragShowNewIndex = undefined
}
</script>

<template>
  <dropdown-menu class="viewmode-dropdown-menu">
    <menu-section>
      <menu-header label="Mode" />
      <div class="all-modes">
        <button
          v-for="[i, mode] in enumerate(Object.values(viewModes))"
          :key="mode.type"
          class="mode"
          :class="{
            selected: mode.type === viewModeState.current.mode,
            icon: mode.type === ViewModeType.Icon,
            grid: mode.type === ViewModeType.Grid,
            detail: mode.type === ViewModeType.Detail,
            feed: mode.type === ViewModeType.Feed,
            column: mode.type === ViewModeType.Column,
            tree: mode.type === ViewModeType.Tree,
            table: mode.type === ViewModeType.Table,
            gallery: mode.type === ViewModeType.Gallery,
          }"
          @click="switchCollectionViewMode(mode.type)"
        >
          <component :is="mode.icon" class="mode-icon" />
          <div class="label">
            {{ mode.name }}
          </div>
          <div class="shortcut">
            {{ `⌘${i + 1}` }}
          </div>
        </button>
      </div>
    </menu-section>
    <template v-if="viewModeState.current.mode === ViewModeType.Detail">
      <menu-separator />
      <menu-section>
        <menu-header label="Options" />
        <div class="flex justify-between">
          <div class="t-menu-sm text-$global-t-secondary">
            Column Width (px)
          </div>
          <menu-selection-slider
            v-model="viewModeState.current.detailView.maxColumnWidth"
            :min="100"
            :max="width"
          />
        </div>
      </menu-section>
    </template>
    <menu-separator />
    <menu-section>
      <menu-header label="Properties" />
      <div class="property-row">
        <div class="property-row-left t-base">
          <grip class="invisible" />
          <icon-property
            size="xs"
            :type="propertyIcon(PropertyId.FILE_NOTES)"
            class="property-icon"
          />
          <div class="property-name">
            Folder Notes
          </div>
        </div>
        <div class="property-row-right">
          <menu-selection
            v-model="viewModeState.current.general.notes"
            :options="[true, false]"
            :label="(isTrue => isTrue ? 'Show' : 'Hide')"
          />
          <icon-action
            size="2xs"
            type="private"
            class="action"
          />
        </div>
      </div>
      <div
        v-for="show, index in viewModeState.current.show"
        :key="show.propertyId"
        :class="{
          dragging: dragState.dragShowIndex === index,
          draggable: index > 0,
        }"
        :draggable="index > 0 ? true : false"
        class="property-row"
        @dragover.prevent
        @dragstart="(event) => startDragShow(event, index)"
        @dragenter="(event) => enterDragShow(event, index)"
        @dragend="(event) => endDragShow(event)"
      >
        <div class="property-row-left t-base">
          <grip class="grip" :class="{ invisible: index === 0 }" />
          <icon-property
            size="xs"
            :type="propertyIcon(show.propertyId)"
            class="property-icon"
          />
          <div class="property-name">
            {{ showableProperties[show.propertyId]!.name }}
          </div>
        </div>
        <div class="property-row-right">
          <menu-selection-slider
            v-if="show.scale !== null"
            v-model="show.scale"
            :min="0"
            :max="1"
            @ended="(scale) => updatePreviewScale(scale, true)"
          />
          <menu-color-selection
            v-if="show.scale === null && show.propertyId !== PropertyId.FILE_VIRTUAL_TAGS"
            v-model="show.color"
            @update:model-value="(c) => updatePropertyColor(show.propertyId, c, true)"
          />
          <menu-selection
            v-if="show.scale === null"
            v-model="show.size"
            :options="[
              PropertySize.Tiny,
              PropertySize.Small,
              PropertySize.Medium,
              PropertySize.Large,
              PropertySize.Huge,
              PropertySize.Giant,
            ]"
            :label="((k) => viewModePropertySizes[k].label)"
            @update:model-value="(s) => updatePropertySize(show.propertyId, s, true)"
          />
          <icon-action
            v-if="show.propertyId === PropertyId.FILE_VIRTUAL_PREVIEW"
            size="2xs"
            type="private"
            class="action"
          />
          <icon-action
            v-else
            action
            size="2xs"
            type="cross"
            class="action button"
            @click.stop.prevent="onToggleShowProperty(show.propertyId)"
          />
        </div>
        <div v-if="dragState.dragShowNewIndex === index" class="drag-indicator" />
      </div>
      <div class="inline-flex">
        <menu-multi-selection
          :options="showablePropertyOptions"
          :label="p => p.name"
          :enabled="p => isShowingProperty(p.propertyId)"
          :menu-width="200"
          @selected="p => onToggleShowProperty(p.propertyId)"
        >
          <div class="multi-select-button">
            <icon-action size="xs" type="plus" />
            <div>Property</div>
          </div>
        </menu-multi-selection>
      </div>
    </menu-section>
    <menu-separator />
    <menu-section>
      <menu-header label="Sort" />
      <div
        v-for="sort in viewModeState.current.sort"
        :key="sort.propertyId"
        class="property-row"
      >
        <div class="property-row-left t-base">
          <grip class="grip" />
          <div class="filter-property-bubble">
            <icon-property
              size="xs"
              :type="propertyIcon(sort.propertyId)"
            />
            <div>
              {{ showableProperties[sort.propertyId]!.name }}
            </div>
          </div>
        </div>
        <div class="property-row-right">
          <menu-selection
            v-model="sort.descending"
            :options="[
              false, true,
            ]"
            :label="(d => d ? 'Descending' : 'Ascending')"
          />
          <icon-action
            action
            size="2xs"
            type="cross"
            class="action button"
            @click.stop.prevent="onToggleSortProperty(sort.propertyId)"
          />
        </div>
      </div>
      <div class="inline-flex">
        <menu-multi-selection
          :options="sortablePropertyOptions"
          :label="p => p.name"
          :enabled="p => isSortingProperty(p.propertyId)"
          :menu-width="200"
          @selected="p => onToggleSortProperty(p.propertyId)"
        >
          <div class="multi-select-button">
            <icon-action size="xs" type="plus" />
            <div>Property</div>
          </div>
        </menu-multi-selection>
      </div>
    </menu-section>
    <menu-separator />
    <menu-section v-if="viewModeState.current.mode === ViewModeType.Gallery">
      <menu-header label="Gallery" />
      <div class="view-mode-option-checkbox">
        <div class="left">
          Show properties
        </div>
        <div class="right">
          <menu-checkbox
            :checked="viewModeState.current.galleryView.properties"
            size="xs"
            @update:checked="(properties: any) => updateGalleryViewSettings({ properties }, true)"
          />
        </div>
      </div>
    </menu-section>
    <!-- TODO persisting view modes -->
    <menu-separator v-if="false" />
    <!-- <menu-section>
      <menu-header label="Filter" />
      <div class="inline-flex">
        <menu-multi-selection
          :options="filterablePropertyOptions"
          :label="p => p.name"
          :enabled="p => isFilteringProperty(p.propertyId)"
          :menu-width="200"
          @selected="p => onToggleFilterProperty(p.propertyId)"
        >
          <div class="multi-select-button">
            <icon-action size="xs" type="plus" />
            <div>Property</div>
          </div>
        </menu-multi-selection>
      </div>
    </menu-section>
    <menu-separator /> -->

    <!-- TODO persisting view modes -->
    <menu-section v-if="false">
      <div class="action-footer">
        <div class="action-reset">
          <span>Reset</span>
          <menu-shortcut>
            ⌥⌘R
          </menu-shortcut>
        </div>
        <menu-button-dropdown
          outlined
          blue
          :emphasis="false"
        >
          <template #default>
            Save
          </template>
          <template #content>
            <div class="save-dropdown">
              <menu-section>
                <menu-header label="Save as default..." />
                TBD
              </menu-section>
              <menu-separator />
              <menu-section>
                <menu-header label="Remove defaults" />
                TBD
              </menu-section>
            </div>
          </template>
        </menu-button-dropdown>
      </div>
    </menu-section>
  </dropdown-menu>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.viewmode-dropdown-menu
  @apply w-[#{$menu-viewmode-w-default}]

.all-modes
  @apply grid grid-cols-2 gap-1 -m-1.5
.mode
  @apply h-8 w-full flex rounded-1.5 p-1.5 items-center gap-1.5 overflow-hidden
  @apply text-$global-t-secondary fw-[#{$global-fw-base-strong}]
  &:hover,&.selected
    &.icon
      @apply text-$global-blue-primary bg-$global-blue-accent
      .shortcut
        @apply bg-$global-blue-accent
    &.grid
      @apply text-$global-pink-primary bg-$global-pink-accent
      .shortcut
        @apply bg-$global-pink-accent
    &.detail
      @apply text-$global-orange-primary bg-$global-orange-accent
      .shortcut
        @apply bg-$global-orange-accent
    &.feed
      @apply text-$global-amber-primary bg-$global-amber-accent
      .shortcut
        @apply bg-$global-amber-accent
    &.column
      @apply text-$global-purple-primary bg-$global-purple-accent
      .shortcut
        @apply bg-$global-purple-accent
    &.tree
      @apply text-$global-green-primary bg-$global-green-accent
      .shortcut
        @apply bg-$global-green-accent
    &.table
      @apply text-$global-teal-primary bg-$global-teal-accent
      .shortcut
        @apply bg-$global-teal-accent
    &.gallery
      @apply text-$global-red-primary bg-$global-red-accent
      .shortcut
        @apply bg-$global-red-accent
  .mode-icon
    @apply flex-none w-[#{$pill-w-icon}] h-[#{$pill-w-icon}]
  .shortcut
    @apply ml-auto bg-[#{$menu-bg-item-hover}] rounded-[#{$menu-br-item-shortcut}] px-[#{$menu-px-item-shortcut}] h-[#{$menu-h-shortcut}] t-menu-2xs inline-flex items-center vertical-middle

.property-row
  @apply flex items-center h-4 justify-between relative max-w-full
  &.dragging
    @apply border-[#{$global-b-outline-page}] z-overlay-layer bg-[#{$menu-bg-default}]
  &.draggable
    .property-row-left
      @apply cursor-grab
  .drag-indicator
    @apply absolute -bottom-1.5 -left-1 -right-1 h-0.25 rounded-full bg-[#{$global-stroke-icon-secondary}]

.property-row-left
  @apply flex items-center h-4 gap-[#{$menu-viewmode-gap-property-edit}] min-w-0
.property-row-right
  @apply flex items-center h-4 gap-[#{$menu-viewmode-gap-property-edit}] flex-none
  .action
    @apply text-[#{$menu-viewmode-bg-property-action}]
    &.button
      @apply cursor-pointer rounded-[2px]
    &:hover
      @apply text-[#{$global-t-default}] bg-$global-red-primary

.property-icon
  @apply text-[#{$global-bg-icon-secondary}] flex-none

.property-name
  @apply h-full text-ellipses-inline min-w-0 inline-flex items-center

.filter-property-bubble
  @apply bg-$global-blue-accent flex items-center  gap-[#{$menu-viewmode-gap-property-edit}] rounded-[#{$page-properties-br-overlay}] px-1 py-0.5 text-$global-blue-primary

.multi-select-button
  @apply flex items-center gap-1 cursor-pointer t-menu-sm text-[#{$menu-viewmode-t-property}] rounded-[#{$menu-br-item}] py-1 pr-1.5 pl-1 -my-1 -mx-1.5
  &:hover
    @apply bg-[#{$menu-bg-item-hover}] text-[#{$global-t-default}]

.grip
  @apply text-[#{$global-bg-icon-secondary-alt}] ml-0.75 flex-none

.action-footer
  @apply flex items-center justify-end gap-[#{$menu-viewmode-gap-persist}]

.action-reset
  @apply flex items-center gap-1 text-[#{$menu-t-header}]

.save-dropdown
  @apply w-35

.view-mode-option-checkbox
  @apply flex items-center justify-between t-menu-sm
</style>
